// useMatches.js
import { useState, useCallback } from "react";
import { getMatches } from "@/services";

const useMatches = () => {
  const [matches, setMatches] = useState([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isMatchesLoading, setIsMatchesLoading] = useState(false);

  const [mlsMatches, setMlsMatches] = useState([]);
  const [totalMlsMatches, setTotalMlsMatches] = useState(0);
  const [hasNextMlsPage, setHasNextMlsPage] = useState(false);
  const [isMlsMatchesLoading, setIsMlsMatchesLoading] = useState(false);

  const fetchMatches = useCallback(async (query = {}, { onSuccess } = {}) => {
    const { wantAdId, inventoryId, mlsListingKey, page = 1 } = query;
    if (!wantAdId && !inventoryId && !mlsListingKey) return;

    setIsMatchesLoading(true);

    try {
      let invData = null;
      let mlsData = null;

      if (wantAdId) {
        // Want-Listing Matches tab is MLS-only — Inventory matches are no
        // longer surfaced here. A single request at the MLS threshold (70,
        // the "perfect fit" cutoff for want-ad ↔ MLS-listing matches) returns
        // the mlsMatches section we render.
        const mlsResp = await getMatches({ ...query, minScore: 70 });
        invData = {};
        mlsData = mlsResp?.data?.mlsMatches || null;
      } else {
        // Inventory reverse-lookup (inventoryId) keeps the standard 60.
        const queryWithScore = { minScore: 60, ...query };
        const { data } = await getMatches(queryWithScore);
        invData = data || {};
        mlsData = data?.mlsMatches || null;
      }

      const invPayload = invData?.data || [];
      setMatches((prev) =>
        page > 1 ? [...prev, ...invPayload] : invPayload,
      );
      setTotalMatches(invData?.totalCount || 0);
      setHasNextPage(!!invData?.hasNextPage);

      if (mlsData) {
        const mlsPayload = mlsData.data || [];
        setMlsMatches((prev) =>
          page > 1 ? [...prev, ...mlsPayload] : mlsPayload,
        );
        setTotalMlsMatches(mlsData.totalCount || 0);
        setHasNextMlsPage(!!mlsData.hasNextPage);
      } else if (page === 1) {
        setMlsMatches([]);
        setTotalMlsMatches(0);
        setHasNextMlsPage(false);
      }

      onSuccess?.();
    } catch {
      if (page === 1) setMatches([]);
      setHasNextPage(false);
    } finally {
      setIsMatchesLoading(false);
    }
  }, []);

  const fetchMlsMatches = useCallback(async (query = {}, { onSuccess } = {}) => {
    const { mlsListingKey, page = 1 } = query;
    if (!mlsListingKey) return;

    setIsMlsMatchesLoading(true);
    // MLS reverse-lookup uses the same 70 "perfect fit" threshold as the MLS
    // section of fetchMatches — keep them in sync.
    const queryWithScore = { minScore: 70, ...query };

    try {
      const { data } = await getMatches(queryWithScore);
      const {
        data: payload = [],
        totalCount = 0,
        hasNextPage: nextPage = false,
      } = data || {};

      setMlsMatches((prev) => (page > 1 ? [...prev, ...payload] : payload));
      setTotalMlsMatches(totalCount);
      setHasNextMlsPage(nextPage);
      onSuccess?.();
    } catch {
      if (page === 1) setMlsMatches([]);
      setHasNextMlsPage(false);
    } finally {
      setIsMlsMatchesLoading(false);
    }
  }, []);

  return {
    matches,
    totalMatches,
    hasNextPage,
    isMatchesLoading,
    fetchMatches,
    mlsMatches,
    totalMlsMatches,
    hasNextMlsPage,
    isMlsMatchesLoading,
    fetchMlsMatches,
  };
};

export default useMatches;
